(() => {
  const TAG = 'teaballs-cart-progress';
  if (customElements.get(TAG)) return;

  const asNumber = (value, fallback = 0) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : fallback;
  };

  const fill = (template, values = {}) => {
    let output = String(template || '');
    for (const [key, value] of Object.entries(values)) {
      output = output.split(`[[${key}]]`).join(String(value));
    }
    return output;
  };

  class TeaballsCartProgress extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const { signal } = this.abort;

      document.addEventListener('teaballs:promotion:ready', () => this.render(), { signal });
      document.addEventListener('teaballs:promotion:change', () => this.render(), { signal });
      // Horizon morphs an existing drawer in place; that does not reconnect this element.
      // Re-render after its embedded cart JSON changes, ignoring our own message updates.
      this.cartObserver?.disconnect();
      this.cartObserver = new MutationObserver(() => {
        const json = this.querySelector('[data-cart-progress-cart]')?.textContent || '';
        if (json !== this.cartSignature) this.render();
      });
      this.cartObserver.observe(this, { childList: true, characterData: true, subtree: true });
      this.render();
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.cartObserver?.disconnect();
    }

    cartData() {
      const node = this.querySelector('[data-cart-progress-cart]');
      if (!node) return null;
      try {
        return JSON.parse(node.textContent || '{}');
      } catch {
        return null;
      }
    }

    shipping(state, promo, cart) {
      if (state?.shipping?.mode === 'free') {
        return { available: true, unlocked: true, thresholdCents: 0, remainingCents: 0 };
      }

      let thresholdCents = asNumber(this.dataset.shippingThreshold, 0);
      const raw = promo?.raw?.();
      const campaignMode = raw?.campaign?.shipping?.mode;

      if (state?.source === 'campaign' && campaignMode === 'threshold') {
        thresholdCents = asNumber(state?.shipping?.thresholdCents, thresholdCents);
      }

      if (thresholdCents <= 0) {
        return { available: false, unlocked: false, thresholdCents: 0, remainingCents: 0 };
      }

      const cartTotal = asNumber(cart?.total_price, 0);
      return {
        available: true,
        unlocked: cartTotal >= thresholdCents,
        thresholdCents,
        remainingCents: Math.max(0, thresholdCents - cartTotal),
      };
    }

    tierState(promo, cart) {
      if (!promo?.surfaceEnabled?.('cart')) return null;
      const state = promo.current?.();
      if (!state || state.offer?.type !== 'tiered') return null;

      const metrics = promo.metricsFromCart?.(cart);
      if (!metrics || asNumber(metrics.eligibleQuantity, 0) <= 0) return null;

      return { state, metrics };
    }

    bottleWord(count) {
      return count === 1 ? this.dataset.labelBottleOne || '' : this.dataset.labelBottleMany || '';
    }

    progressForShipping(cart, shipping) {
      if (!shipping.available || shipping.thresholdCents <= 0) return 0;
      const cartTotal = asNumber(cart?.total_price, 0);
      return Math.max(0, Math.min(100, (cartTotal / shipping.thresholdCents) * 100));
    }

    progressForTier(metrics) {
      const quantity = asNumber(metrics?.eligibleQuantity, 0);
      const currentMin = asNumber(metrics?.tier?.current?.minQuantity, 0);
      const nextMin = asNumber(metrics?.tier?.next?.minQuantity, 0);
      if (!nextMin) return quantity > 0 ? 100 : 0;
      const span = Math.max(1, nextMin - currentMin);
      return Math.max(0, Math.min(100, ((quantity - currentMin) / span) * 100));
    }

    renderTierMessage(tier, shippingUnlocked) {
      const current = tier.metrics?.tier?.current;
      const next = tier.metrics?.tier?.next;
      const remaining = asNumber(tier.metrics?.tier?.remaining, 0);
      const currentDiscount = asNumber(current?.discountPercent, 0);
      const nextDiscount = asNumber(next?.discountPercent, 0);

      if (next) {
        if (current) {
          return fill(
            shippingUnlocked ? this.dataset.labelMixNextCurrent : this.dataset.labelMixNextCurrentNoShipping,
            {
              current: currentDiscount,
              count: remaining,
              bottle: this.bottleWord(remaining),
              next: nextDiscount,
            }
          );
        }
        return fill(
          shippingUnlocked ? this.dataset.labelMixNext : this.dataset.labelMixNextNoShipping,
          {
            count: remaining,
            bottle: this.bottleWord(remaining),
            next: nextDiscount,
          }
        );
      }

      if (current) {
        return fill(
          shippingUnlocked ? this.dataset.labelMixMax : this.dataset.labelMixMaxNoShipping,
          { current: currentDiscount }
        );
      }

      return '';
    }

    renderMessage(text, tier, shipping) {
      const lines = String(text).split(' · ');
      let status = lines.shift();
      let nextStep = lines.join(' · ');

      if (this.dataset.customIcons === 'true') {
        const current = tier?.metrics?.tier?.current;
        const next = tier?.metrics?.tier?.next;
        let icon = this.dataset.iconPending;
        if (current) {
          icon = next ? this.dataset.iconMix : this.dataset.iconMax;
        } else if (shipping.available && shipping.unlocked) {
          icon = this.dataset.iconShipping;
        }
        status = status.replace(/^\s*✓\s*/, '');
        status = [icon?.trim(), status].filter(Boolean).join(' ');
        if (nextStep) {
          nextStep = [this.dataset.iconNext?.trim(), nextStep].filter(Boolean).join(' ');
        }
      }

      return [status, nextStep].filter(Boolean).join('\n');
    }

    render() {
      this.cartSignature = this.querySelector('[data-cart-progress-cart]')?.textContent || '';
      const cart = this.cartData();
      if (!cart) return;

      const promo = window.TeaballsPromotion;
      const state = promo?.current?.();
      const shipping = this.shipping(state, promo, cart);
      const tier = this.tierState(promo, cart);
      const message = this.querySelector('[data-cart-progress-message]');
      const bar = this.querySelector('[data-cart-progress-fill]');
      const progressbar = this.querySelector('[data-cart-progress-track]');
      if (!message || !bar || !progressbar) return;

      let text = '';
      let percent = 0;

      if (shipping.available && !shipping.unlocked) {
        const amount = promo?.formatMoney
          ? promo.formatMoney(shipping.remainingCents)
          : this.dataset.shippingRemainingFormatted || '';
        const currentDiscount = asNumber(tier?.metrics?.tier?.current?.discountPercent, 0);
        if (currentDiscount > 0) {
          text = fill(this.dataset.labelMixShippingRemaining, {
            current: currentDiscount,
            amount,
          });
        } else {
          text = fill(this.dataset.labelShippingRemaining, { amount });
        }
        percent = this.progressForShipping(cart, shipping);
      } else if (tier) {
        text = this.renderTierMessage(tier, shipping.available && shipping.unlocked);
        percent = this.progressForTier(tier.metrics);
      } else if (shipping.available && shipping.unlocked) {
        text = this.dataset.labelShippingReached || '';
        percent = 100;
      }

      if (!text) {
        this.hidden = true;
        return;
      }

      this.hidden = false;
      message.textContent = this.renderMessage(text, tier, shipping);
      bar.style.width = `${percent}%`;
      progressbar.setAttribute('aria-valuenow', String(Math.round(percent)));
      progressbar.setAttribute('aria-label', text);
    }
  }

  customElements.define(TAG, TeaballsCartProgress);
})();
